import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

type TimelineItemData = {
  id: string | number;
  title: string;
  desc: string;
  img: string;
  forceRight?: boolean;
};

type TimelineItemProps = {
  item: TimelineItemData;
  index: number;
};

export default function TimelineItem({ item, index }: TimelineItemProps) {
  const reverse = item.forceRight || index % 2 === 1;

  const ref = useRef<HTMLDivElement | null>(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.9", "end 0.1"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["-60px", "60px"]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 80 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={`relative grid md:grid-cols-2 gap-16 items-center ${
        reverse ? "md:[&>*:first-child]:order-2" : ""
      }`}
    >
      {/* Parallax Image */}
      <motion.div
        style={{ y }}
        className="w-full group relative overflow-hidden rounded-xl shadow-xl shadow-black/50"
      >
        <img
          src={item.img}
          className="w-full h-[330px] object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-40 transition duration-700"></div>
      </motion.div>

      {/* Center timeline glowing dot */}
      <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 pointer-events-none">
        <div className="w-4 h-4 bg-red-500 rounded-full shadow-[0_0_25px_8px_rgba(255,0,0,0.45)] relative">
          <span className="absolute inset-0 w-full h-full bg-red-500 rounded-full blur-xl opacity-70 animate-ping"></span>
        </div>
      </div>

      {/* Text */}
      <div className={`text-gray-300 space-y-2 ${(index%2)==1?'text-right':''}`}>
        <p className="text-sm text-red-400 font-semibold tracking-widest">
          {item.id}
        </p>
        <h3 className="text-3xl font-bold text-black">{item.title}</h3>
        <p className="leading-relaxed text-gray-700">{item.desc}</p>
      </div>
    </motion.div>
  );
}
